import type { Path } from '@ahm/api-wrappers-core';
import { formatCurrency } from '@ahm/common-helpers';
import { FloatingLabelNumberInput } from '@ahm/react-ui';
import { ActionIcon, Box, Card, Group, Stack, Text, rem } from '@mantine/core';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import XCircle from '@/assets/icons/xcircle-icon.svg';
import InfoTooltip from '@/components/info-tooltip';
import { ROUTES } from '@/config/router';
import {
  insuranceOptionsBike,
  insuranceOptionsTruck,
  MAX_ITEM_VALUE_DEFAULT,
} from '@/lib/constants';
import { findInsuranceInRequests } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';
import { InsurancePackageType } from '@/types/booking';

type PathItemValueProps = {
  handleInputChange: (field: keyof Path, value: number | string) => void;
};

export default function PathItemValue({
  handleInputChange,
}: PathItemValueProps) {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    trigger,
    watch,
  } = useFormContext();
  const tBooking = useTranslations('booking');
  const pathname = usePathname();
  const { selectedService } = useBookingStore((state) => state);
  const [compensation, setCompensation] = useState<number>(0);
  const [isFocusedItemValue, setIsFocusedItemValue] = useState(false);

  const insuranceRequest = useMemo(() => {
    return selectedService ? findInsuranceInRequests(selectedService) : null;
  }, [selectedService]);

  const handleOpenTooltip = () => {
    const benefitUrl = insuranceRequest?.benefit_url;

    if (!benefitUrl) return;

    window.open(benefitUrl, '_blank');
  };

  const maxItemValue = useMemo(() => {
    const maxItemValueByService = insuranceRequest?.max_item_value;

    let defaultMaxItemValue = MAX_ITEM_VALUE_DEFAULT.BIKE;

    if (pathname === ROUTES.truck) {
      defaultMaxItemValue = MAX_ITEM_VALUE_DEFAULT.TRUCK;
    }

    return maxItemValueByService ?? defaultMaxItemValue;
  }, [insuranceRequest?.max_item_value, pathname]);

  const handleChangeItemValue = useCallback(
    async (value: string | number) => {
      handleInputChange('item_value', value);

      await trigger('item_value');

      if (Number(value) > maxItemValue) {
        setError('item_value', {
          type: 'max',
          message: tBooking('insurance.error.item_value_max', {
            max: formatCurrency(maxItemValue),
          }),
        });
      } else {
        clearErrors('item_value');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxItemValue]
  );

  const insuranceOptions = useMemo(() => {
    if (pathname === ROUTES.truck) {
      return insuranceOptionsTruck;
    }
    return insuranceOptionsBike;
  }, [pathname]);

  const updateCompensation = (value: number) => {
    const selectedInsuranceOption = insuranceOptions.find(
      (option) =>
        option.from !== undefined &&
        option.to !== undefined &&
        Number(value) >= option.from &&
        Number(value) < option.to
    );

    // remove option id is None then get the last option
    const removeOptionNone = insuranceOptions.filter(
      (option) => option.id !== InsurancePackageType.None
    );

    const lastInsuranceOption =
      removeOptionNone.slice(-1)[0]?.compensation ?? 0;

    const finalCompensation =
      selectedInsuranceOption?.compensation ?? lastInsuranceOption;

    setCompensation(finalCompensation);
  };

  const itemValue = watch('item_value');
  useEffect(() => {
    if (itemValue >= 0) {
      updateCompensation(itemValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemValue]);

  return (
    <Card radius={0} px="md">
      <Card.Section inheritPadding py="sm">
        <Group gap={rem(4)}>
          <Text fz={16} fw={700} lts={0.5} c="neutral.7">
            {tBooking('insurance.item_value')}
          </Text>

          {insuranceRequest?.benefit_url ? (
            <Box mt={rem(-4)}>
              <InfoTooltip
                label={tBooking('insurance.item_value')}
                onClick={handleOpenTooltip}
              />
            </Box>
          ) : null}
        </Group>
      </Card.Section>

      <Stack gap={rem(10)} p="md" pt={0} component={Card.Section}>
        <Controller
          name="item_value"
          control={control}
          render={({ field }) => (
            <FloatingLabelNumberInput
              {...field}
              styles={{
                input: {
                  borderColor: errors.item_value
                    ? 'var(--mantine-color-red-6)'
                    : isFocusedItemValue
                      ? 'var(--mantine-color-secondary-4)'
                      : 'var(--mantine-color-neutral-2)',
                },
              }}
              onFocus={() => setIsFocusedItemValue(true)}
              onBlur={() => setIsFocusedItemValue(false)}
              min={0}
              label={tBooking('insurance.item_value')}
              suffix="đ"
              rightSection={
                <>
                  {field.value ? (
                    <ActionIcon
                      aria-label="Clear item_value"
                      variant="transparent"
                      onClick={() => {
                        void handleChangeItemValue(0);
                      }}
                    >
                      <XCircle />
                    </ActionIcon>
                  ) : null}
                </>
              }
              allowLeadingZeros
              onChange={(value) => {
                void handleChangeItemValue(value);
              }}
              error={errors.item_value?.message as string}
            />
          )}
        />

        <Text size="xs" fw={500} c="black" fs="italic">
          {tBooking('insurance.compensation_infor', {
            compensation: formatCurrency(compensation),
          })}
        </Text>
      </Stack>
    </Card>
  );
}
